// UI-only period filter for the LLM monitoring page (admin panel P1).
//
// Owns the preset→window mapping so the page never duplicates it: presets
// are computed at selection time (not per render), so a stable window stays
// in the query key between refetches. The custom range is a dayjs RangePicker
// — antd locale follows ConfigProvider (ru/en).
import type { ReactElement } from 'react';
import { DatePicker, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@govard/i18n-core/react';

import type { LlmPeriod } from '../api/admin.client.js';

export type LlmPreset = '7d' | '30d' | '90d' | 'custom';

export interface LlmPeriodState {
  preset: LlmPreset;
  period: LlmPeriod;
}

const PRESET_DAYS: Record<Exclude<LlmPreset, 'custom'>, 7 | 30 | 90> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

/** Window for a days-preset, computed once at selection/query time. */
export function presetPeriod(days: 7 | 30 | 90): LlmPeriod {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function defaultLlmPeriodState(): LlmPeriodState {
  return { preset: '7d', period: presetPeriod(7) };
}

const PRESET_OPTIONS: readonly LlmPreset[] = ['7d', '30d', '90d', 'custom'];

export function LlmPeriodFilter({
  value,
  onChange,
}: {
  value: LlmPeriodState;
  onChange: (next: LlmPeriodState) => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      <Select<LlmPreset>
        aria-label={t('crm.admin.llm.period_label')}
        value={value.preset}
        onChange={(preset) => {
          if (preset === 'custom') {
            // Keep the current window until the admin picks a range.
            onChange({ preset, period: value.period });
            return;
          }
          onChange({ preset, period: presetPeriod(PRESET_DAYS[preset]) });
        }}
        options={PRESET_OPTIONS.map((preset) => ({
          value: preset,
          label:
            preset === 'custom'
              ? t('crm.admin.llm.period_custom')
              : t('crm.admin.llm.period_days', { days: PRESET_DAYS[preset] }),
        }))}
        style={{ width: 150 }}
      />
      {value.preset === 'custom' ? (
        <DatePicker.RangePicker
          aria-label={t('crm.admin.llm.range_label')}
          value={[dayjs(value.period.from), dayjs(value.period.to)]}
          allowEmpty={[false, false]}
          onChange={(range) => {
            if (range === null) return;
            const [from, to] = range as [Dayjs | null, Dayjs | null];
            if (from === null || to === null) return;
            onChange({
              preset: 'custom',
              period: { from: from.toISOString(), to: to.toISOString() },
            });
          }}
        />
      ) : null}
    </div>
  );
}
